/**
 * @memberof @albert-io/notifier
 */
import React from 'react';
import {getEnvironment} from '@albert-io/environment';
import {Honeybadger, HoneybadgerErrorBoundary} from '@honeybadger-io/react';

import {ERROR_TYPES} from 'client/constants';
import ErrorPage from 'client/static/Error/ErrorPage.react';
import sessionStore from 'client/Session/SessionStore';
import notifier from '../../notifier';

const contentFilters = [
  'token',
  'email',
  'username',
  'password',
  'verify_old_password',
  'password_reset_link',
  'verify_password_reset_link',
  'stripe_customer_id',
  'first_name',
  'last_name',
  'salutation',
  'consent_email'
];

function isApiError(notice) {
  const message = notice.message || ''; // Safe fallback to empty string
  // check for string patterns that indicate API errors
  return (
    message.includes('"errors":[') && message.includes('"code":') && message.includes('"status":')
  );
}
const getVersionFromStaticAssetsPath = () => {
  const path = process.env.STATIC_ASSETS_APP_PATH || '';
  if (!path) return '';

  // Split by '/' and get the last non-empty segment
  const segments = path.split('/').filter((segment) => segment.length > 0);
  return segments.length > 0 ? segments[segments.length - 1] : '';
};

const release_version = getVersionFromStaticAssetsPath();

const isCurrentUrlMatch = (match) => {
  if (!match) {
    return false;
  }
  const {pathname} = window.location;
  return pathname.includes(match);
};

function startHoneybadger() {
  Honeybadger.configure({
    apiKey: process.env.HONEYBADGER_API_KEY,
    environment: getEnvironment(),
    filters: contentFilters,
    reportData: true,
    revision: release_version,
    maxErrors: 20,
    breadcrumbsEnabled: {
      dom: true,
      network: true,
      navigation: true,
      console: false
    },
    maxBreadcrumbs: 50
  });

  // Set up beforeNotify handler
  Honeybadger.beforeNotify(function (notice) {
    const userAgent = navigator.userAgent || '';
    const isIpad = /iPad/.test(userAgent);
    const isDennis = isCurrentUrlMatch('/dennis');
    const tags = [
      release_version,
      sessionStore.hasValidSession() ? sessionStore.getAccountType() : 'unauthenticated',
      isIpad && 'ipad',
      isDennis && 'dennis'
    ]
      .filter(Boolean)
      .join(',');

    const hasTranslate = global.document.getElementsByClassName('translated-ltr').length > 0;

    const user = notifier.getIdentity();

    // Merge existing context with new context instead of overwriting
    notice.context = {
      ...notice.context, // Preserve existing context from notifier.notify()
      user_id: user.id,
      user,
      hasTranslate,
      _component: notice.component,
      route: {pathname: window.location.pathname},
      tags: tags
    };

    // Safe message checking - ensure notice.message exists before using .includes()
    const message = notice.message || '';

    // ignore UnhandledPromiseRejectionWarning: Unspecified reason: https://docs.honeybadger.io/lib/javascript/guides/reducing-noise/#transient-unhandledpromiserejectionwarning-errors
    if (/UnhandledPromiseRejectionWarning: Unspecified reason/.test(message)) {
      return false;
    }
    // ignore UnhandledPromiseRejectionWarning: Object Not Found errors
    if (/UnhandledPromiseRejectionWarning: Object Not Found Matching Id/.test(message)) {
      return false;
    }
    // ignore API errors: https://github.com/albert-io/nexus/issues/13502
    if (message && isApiError(notice)) {
      return false; // Suppress the notice
    }
    // ignore JSXGraph error: https://github.com/albert-io/nexus/issues/13401
    if (
      notice.name === 'TypeError' &&
      message.includes("Cannot read properties of null (reading 'insertBefore')")
    ) {
      return false;
    }
    // ignore SyntaxError: Unexpected token '<'
    if (notice.name === 'SyntaxError' && message.includes("Unexpected token '<'")) {
      return false;
    }
    // ignore errors coming from jam.dev
    if (notice.stack && /https:\/\/js.jam.dev/.test(notice.stack)) {
      return false;
    }

    // ignore errors coming from GoGuardian assets
    if (notice.stack && /https:\/\/asset.goguardian\//.test(notice.stack)) {
      return false;
    }

    // ignore errors coming from Google (GTM/GA)
    if (notice.stack && /https:\/\/.*\.google\.com/.test(notice.stack)) {
      return false;
    }

    // ignore slate error: https://github.com/albert-io/nexus/issues/13713#issuecomment-3428391193
    // unignore once we upgrade slate to latest (react v18 req)
    if (message.includes('Cannot resolve a Slate point from DOM point')) {
      return false;
    }

    // ignore ipad max call stack error: https://github.com/albert-io/nexus/issues/13786
    if (
      message.includes('Maximum call stack size exceeded') &&
      isIpad &&
      isCurrentUrlMatch('/assignment/')
    ) {
      return false;
    }

    // ignore audio player error: https://github.com/albert-io/nexus/issues/13438#event-19591642041
    if (notice.name === 'AbortError' && message.includes('aborted')) {
      const hasAudioPlayer =
        !!global.document.getElementsByClassName('audio-player__button').length;
      return hasAudioPlayer ? false : true;
    }
    // ignore terminated requests: https://github.com/albert-io/nexus/issues/13714
    if (/Request has been terminated/.test(message)) {
      return false;
    }
    // ignore chunk errors: https://github.com/albert-io/nexus/issues/14032
    if (/Loading (CSS )?chunk [0-9]+ failed/.test(message)) {
      return false;
    }

    // ignore can't find variable gmo error
    if (/Can't find variable: gmo/.test(notice.message)) {
      return false;
    }

    return true;
  });
}

if (process.env.HONEYBADGER_API_KEY) {
  startHoneybadger();
  notifier.setClient(Honeybadger);

  notifier.setNotifyHandler((error, context) => {
    Honeybadger.notify(error, context); // Pass context to Honeybadger
  });
}

export const ErrorBoundary = ({children}) => (
  <HoneybadgerErrorBoundary
    honeybadger={Honeybadger}
    ErrorComponent={() => <ErrorPage errorCode={ERROR_TYPES.GENERIC} />}
  >
    {children}
  </HoneybadgerErrorBoundary>
);

export default notifier;
